import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { StateManagerProps } from "react-select/dist/declarations/src/stateManager";
import { getPropertiesSelectQuery } from "rsl-api-106/lib/queries";
import { Property, Query, QueryGetPropertiesArgs } from "rsl-api-106/lib/types";

import { useQuery } from "@apollo/client";
import AdvancedSelect from "@components/_Layout/Controls/AdvancedSelect";
import { useSession } from "@hooks";

import { SelectOptionType } from "../../../../../types";

interface SearchDestinationProps extends StateManagerProps {
  onDefaultValueSet?: (
    value: SelectOptionType[] | SelectOptionType | null
  ) => void;
}

const languagesVarsCodes = [
  "LANG_neobooking_etablishment_loading", //Chargement....
  "LANG_neobooking_etablishment_no_results", //Aucun résultats
  "LANG_neobooking_etablishment", //Destinations
];

const SearchDestination: React.FC<SearchDestinationProps> = ({
  onDefaultValueSet,
  ...props
}) => {
  /* Hooks */
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionId, getLanguageVarValue } = useSession();
  const { data, loading, error } = useQuery<Query, QueryGetPropertiesArgs>(
    getPropertiesSelectQuery(),
    {
      variables: {
        authentication: {
          username:
            searchParams.get("webuser") ??
            searchParams.get("wu") ??
            process.env.REACT_APP_WEBUSER!!,
          password: process.env.REACT_APP_PASSWORD!!,
        },
        input: {},
      },
      fetchPolicy: "no-cache",
    }
  );

  /* State */
  const [isMultiDestination, setIsMultiDestination] = useState<boolean>(
    props.isMulti ?? false
  );
  const [destinationsOptions, setDestinationsOptions] = useState<
    SelectOptionType[]
  >([]);
  const [destinationPlaceholder, setDestinationPlaceholder] =
    useState<string>("Chargement...");

  /* This is a way to set a default value for the select component. */
  useEffect(() => {
    if (destinationsOptions.length === 1) {
      onDefaultValueSet &&
        onDefaultValueSet(destinationsOptions[0] as SelectOptionType);
    }

    //Autobind values to the search params
    const etabsIds = searchParams.get("etabIDs");
    if (etabsIds) {
      const etabsIdsArray = etabsIds.split(",");
      const selectedEtabsOptions = etabsIdsArray.map((etabId: string) => {
        const etabOption = destinationsOptions.find(
          (option) => option.value === etabId
        );
        if (etabOption) {
          return etabOption;
        }
      });

      if (isMultiDestination) {
        onDefaultValueSet &&
          onDefaultValueSet(
            selectedEtabsOptions.filter(
              (x) => x !== undefined
            ) as SelectOptionType[]
          );
      } else {
        if (selectedEtabsOptions.length > 0 && selectedEtabsOptions[0]) {
          onDefaultValueSet &&
            onDefaultValueSet(selectedEtabsOptions[0] as SelectOptionType);
        }
      }
    }
  }, [destinationsOptions]);

  useEffect(() => {
    if (data && data.getProperties?.properties) {
      initDestinationList(data.getProperties?.properties as Property[]);
    }
  }, [data]);

  const initDestinationList = (etabs: Property[]) => {
    const opt: SelectOptionType[] = [];
    etabs.forEach((item) => {
      opt.push({ label: item.label!!, value: item?.id?.toString()!! });
    });

    setDestinationsOptions(opt);
    setDestinationPlaceholder(
      getLanguageVarValue("LANG_neobooking_etablishment")
    );
  };

  return (
    <AdvancedSelect
      name="selectDestination"
      options={destinationsOptions}
      placeholder={destinationPlaceholder}
      isLoading={loading}
      noOptionsMessage={() => (
        <>{getLanguageVarValue("LANG_neobooking_etablishment_no_results")}</>
      )}
      loadingMessage={() => (
        <>{getLanguageVarValue("LANG_neobooking_etablishment_loading")}</>
      )}
      {...props}
      isMulti={isMultiDestination}
      isClearable={destinationsOptions.length !== 1}
    />
  );
};

SearchDestination.propTypes = {
  //
};

export default SearchDestination;
